import { type FormEvent, useState } from "react";

import Button from "../components/Button";
import { signInMessage, useAuth } from "../lib/auth";
import "./LoginPage.css";

/** The front door.
 *
 *  Email and password only, and no way to create an account. Managers are
 *  added in the Firebase console rather than signing themselves up: this is
 *  an internal tool holding unannounced projects, so an open registration
 *  form would be a hole rather than a feature. */
export default function LoginPage() {
  const { signIn, resetPassword, configured } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSent(false);
    setBusy(true);
    try {
      await signIn(email, password);
      // Nothing to do on success. The auth listener swaps this screen out.
    } catch (err) {
      setError(signInMessage((err as { code?: string })?.code ?? ""));
    } finally {
      setBusy(false);
    }
  }

  async function onReset() {
    setError(null);
    setSent(false);
    if (!email.trim()) {
      setError("Enter your email address first, then ask for a reset link.");
      return;
    }
    setBusy(true);
    try {
      await resetPassword(email);
      // Says "if" on purpose. Confirming an address has an account would let
      // the form be used to find out who works here.
      setSent(true);
    } catch (err) {
      setError(signInMessage((err as { code?: string })?.code ?? ""));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__mark" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M9 1.8l1.9 4.4 4.4 1.9-4.4 1.9L9 14.4l-1.9-4.4L2.7 8.1l4.4-1.9L9 1.8Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className="login__wordmark">CastFlow</span>
        </div>

        <h1>Sign in</h1>
        <p className="login__sub">
          Your projects, roles and client matches in one place.
        </p>

        {!configured ? (
          <p className="login__error" role="alert">
            This build has no Firebase project configured, so signing in is not
            possible. The four VITE_FIREBASE_ values need to be set at build
            time.
          </p>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <label className="login__field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="you@company.com"
                required
                autoFocus
              />
            </label>

            <label className="login__field">
              <span>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            {error && (
              <p className="login__error" role="alert">
                {error}
              </p>
            )}
            {sent && (
              <p className="login__ok" role="status">
                If that address has an account, a reset link is on its way.
              </p>
            )}

            <Button type="submit" disabled={busy} className="login__submit">
              {busy ? "Signing in..." : "Sign in"}
            </Button>

            <button className="login__link" type="button" onClick={onReset} disabled={busy}>
              Forgot your password?
            </button>
          </form>
        )}

        <p className="login__foot">
          Accounts are created by an administrator. Ask if you need one.
        </p>
      </div>
    </main>
  );
}
